import { component } from '@diligentcorp/atlas-design-tokens/lens';
import type {
  LensPresets,
  LensTheme,
} from '@diligentcorp/atlas-theme-mui-lens';
import {
  Box,
  CircularProgress,
  Drawer,
  Pagination,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import emptyStateIllustration from 'src/components/CategoryConfiguration/assets/atlas-empty-state.svg';
import { EmptyState } from 'src/components/atlas/molecules/EmptyState';
import {
  ActivityLogPaginationType,
  FormManagementActivityLogEntry,
  useFormManagementActivityLogQuery,
  useFormManagementQuestionnaireActivityLogQuery,
} from 'src/graphql';

const formatTimestamp = (iso: string): string =>
  dayjs(iso).format('YYYY-MM-DD HH:mm');

export type FormAuditLogTab = 'Speak Up' | 'Conflict of Interest';

const ENTRIES_PER_PAGE = 10;
const AUDIT_LOG_TABS: FormAuditLogTab[] = ['Speak Up', 'Conflict of Interest'];

const TAB_DISPLAY_LABELS: Record<FormAuditLogTab, string> = {
  'Speak Up': 'Speak up',
  'Conflict of Interest': 'Conflict of interest',
};
const COLUMNS = ['User', 'Form title', 'Event', 'Timestamp'];

const EMPTY_STATE = (
  <EmptyState
    title="No audit logs yet"
    description="There are no audit log entries yet"
    illustration={
      <img
        src={emptyStateIllustration}
        alt=""
        aria-hidden="true"
        data-testid="empty-state-illustration"
        width={144}
        height={144}
      />
    }
  />
);

const ERROR_STATE = (
  <EmptyState
    title="Couldn't load audit log"
    description="Try closing and reopening the panel. If this keeps happening, contact support."
  />
);

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  enabledTabs?: FormAuditLogTab[];
  categoryIdMap?: Partial<Record<FormAuditLogTab, string>>;
  questionnaireId?: string | null;
  questionnaireTitle?: string;
}

export const FormAuditLogTable = ({
  isOpen,
  setIsOpen,
  enabledTabs,
  categoryIdMap,
  questionnaireId,
  questionnaireTitle,
}: Props) => {
  const { presets } = useTheme<LensTheme>();
  const SideSheetPresets = presets?.SideSheetPresets;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={() => setIsOpen(false)}
      sx={SideSheetPresets ? { ...SideSheetPresets.size.large.sx } : undefined}
      data-testid="form-audit-log-slideout"
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': 'form-audit-log-title',
        },
      }}
      keepMounted
    >
      <DrawerBody
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        enabledTabs={enabledTabs}
        categoryIdMap={categoryIdMap}
        questionnaireId={questionnaireId}
        questionnaireTitle={questionnaireTitle}
        SideSheetPresets={SideSheetPresets}
      />
    </Drawer>
  );
};

type SideSheetPresetsShape = LensPresets['SideSheetPresets'];

interface DrawerBodyProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  enabledTabs?: FormAuditLogTab[];
  categoryIdMap?: Partial<Record<FormAuditLogTab, string>>;
  questionnaireId?: string | null;
  questionnaireTitle?: string;
  SideSheetPresets?: SideSheetPresetsShape;
}

const DrawerBody: React.FC<DrawerBodyProps> = ({
  isOpen,
  setIsOpen,
  enabledTabs,
  categoryIdMap,
  questionnaireId,
  questionnaireTitle,
  SideSheetPresets,
}) => {
  const isPerQuestionnaire = !!questionnaireId;
  const tabsToShow = enabledTabs ?? AUDIT_LOG_TABS;
  const firstTab = tabsToShow[0];

  const [selectedTab, setSelectedTab] = useState<FormAuditLogTab>(
    firstTab ?? 'Speak Up',
  );
  const [currentPage, setCurrentPage] = useState(1);

  const effectiveTab = tabsToShow.includes(selectedTab)
    ? selectedTab
    : (firstTab ?? 'Speak Up');

  const formCategoryId = categoryIdMap?.[effectiveTab];

  const orgQuery = useFormManagementActivityLogQuery(
    {
      filters: {
        page: currentPage,
        limit: ENTRIES_PER_PAGE,
        formCategoryId,
      },
    },
    { enabled: isOpen && !isPerQuestionnaire && tabsToShow.length > 0 },
  );

  const questionnaireQuery = useFormManagementQuestionnaireActivityLogQuery(
    {
      questionnaireId: questionnaireId ?? '',
      filters: { page: currentPage, limit: ENTRIES_PER_PAGE },
    },
    { enabled: isOpen && isPerQuestionnaire },
  );

  const activeQuery = isPerQuestionnaire ? questionnaireQuery : orgQuery;
  const activeData = isPerQuestionnaire
    ? questionnaireQuery.data?.formManagementQuestionnaireActivityLog
    : orgQuery.data?.formManagementActivityLog;

  const entries: FormManagementActivityLogEntry[] = activeData?.data ?? [];
  const pagination: ActivityLogPaginationType | undefined | null =
    activeData?.pagination;
  const isLoading = activeQuery.isLoading;
  const isError = activeQuery.isError;
  const totalPages =
    pagination?.totalPages ??
    (pagination?.totalItems
      ? Math.ceil(pagination.totalItems / ENTRIES_PER_PAGE)
      : pagination?.hasNext
        ? currentPage + 1
        : 1);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTab]);

  const handleTabChange = useCallback(
    (_: React.SyntheticEvent, value: FormAuditLogTab) => {
      setSelectedTab(value);
    },
    [],
  );

  const showTabs = !isPerQuestionnaire && tabsToShow.length >= 1;
  const neitherEnabled = !isPerQuestionnaire && tabsToShow.length === 0;
  const slideOutTitle = isPerQuestionnaire
    ? `Audit log — ${questionnaireTitle ?? 'Form'}`
    : 'Audit log';

  const renderBody = (): ReactNode => {
    if (isLoading) {
      return (
        <Box
          data-testid="form-audit-log-loading"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (isError) return ERROR_STATE;
    if (neitherEnabled) {
      return (
        <EmptyState
          title="No audit log available"
          description="No form types are enabled for audit log."
        />
      );
    }
    if (entries.length === 0) {
      return <div data-testid="audit-log-empty">{EMPTY_STATE}</div>;
    }
    return (
      <Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table
            aria-label="Form audit log"
            size="small"
            data-testid="form-audit-log-table"
          >
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell key={col}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry, index) => {
                const truncatedCellSx = {
                  maxWidth: 160,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                } as const;
                const truncCell = (value: string) => (
                  <Tooltip title={value} placement="top">
                    <span>{value}</span>
                  </Tooltip>
                );
                const user = entry.user?.name ?? entry.user?.email ?? '-';
                const title = entry.parentEntityName ?? entry.entityName ?? '-';
                const event = entry.eventDisplayName ?? entry.action ?? '-';
                const timestamp = entry.timestamp
                  ? formatTimestamp(entry.timestamp)
                  : '-';
                return (
                  <TableRow
                    key={entry.id ?? `${entry.action}-${index}`}
                    data-testid={`audit-log-row-${entry.id ?? index}`}
                  >
                    <TableCell sx={truncatedCellSx}>
                      {truncCell(user)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...truncatedCellSx,
                        textDecoration: 'underline',
                      }}
                    >
                      {truncCell(title)}
                    </TableCell>
                    <TableCell sx={truncatedCellSx}>
                      {truncCell(event)}
                    </TableCell>
                    <TableCell sx={truncatedCellSx}>
                      {truncCell(timestamp)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        {totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: component.table.footer.container.marginTop.value,
            }}
          >
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_event, value) => setCurrentPage(value)}
              data-testid="audit-log-pagination"
            />
          </Box>
        )}
      </Box>
    );
  };

  const Header = SideSheetPresets?.components?.Header;
  const Content = SideSheetPresets?.components?.Content;

  return (
    <>
      {Header ? (
        <Header
          variant="default"
          onClose={() => setIsOpen(false)}
          title={slideOutTitle}
          componentProps={{
            closeButton: { 'aria-label': 'Close audit log' },
            title: { component: 'h2', id: 'form-audit-log-title' },
          }}
        />
      ) : (
        <Box component="h2" id="form-audit-log-title" sx={{ m: 0, p: 2 }}>
          {slideOutTitle}
        </Box>
      )}
      {showTabs && (
        <Box
          sx={{
            marginTop: component.sideSheet.gap.value,
            paddingLeft: component.sideSheet.paddingLeft.value,
            paddingRight: component.sideSheet.paddingRight.value,
          }}
        >
          <Tabs
            value={effectiveTab}
            onChange={handleTabChange}
            aria-label="Form audit log tabs"
            data-testid="form-audit-log-tabs"
          >
            {tabsToShow.map((tab, index) => (
              <Tab
                key={tab}
                value={tab}
                label={TAB_DISPLAY_LABELS[tab] ?? tab}
                data-testid={`form-audit-log-tab-${index + 1}`}
              />
            ))}
          </Tabs>
        </Box>
      )}
      {Content ? (
        <Content
          ariaLabel="Form audit log entries"
          data-testid="form-audit-log-content"
        >
          {renderBody()}
        </Content>
      ) : (
        <Box data-testid="form-audit-log-content">{renderBody()}</Box>
      )}
    </>
  );
};
